import React, { useCallback, useEffect, useState } from 'react';
import { useSetRecoilState } from 'recoil';
import { css } from '@emotion/react';
import { Input, PrimaryButton, SelectSearchDicomTag } from '@components/atoms';
import { dialogContentsState } from '@components/templates/Dialog';
import { getFetcher, PRODUCT_CODE } from '../../../../App';
import { DicomInfo } from '@pages/AppSetting/CTP/SendToPacs';
import SettingCommonMethods from 'utils/settingCommon';
import useStaticDialog from 'utils/hooks/useStaticDialog';
import { submitDebounce } from 'utils/lib';
import {
  StyledMetatagItem,
  StyledMetatagBox,
  StyledMetatagItemWrap,
  StyledValue,
  StyledMetatag,
  StyledTxtSuccess,
  StyledConfirmButton,
  StyledcloseButton,
  StyledMetatagAddButton,
  StyledSwButtonSection,
} from './style';
import he from 'he';

const CustomMetaTag = () => {
  const [dicomTagList, setDicomTagList] = useState<DicomInfo[]>([]);
  const [isChanged, setIsChanged] = useState(false);

  // result
  const [metaList, setMetaList] = useState<DicomInfo[]>([]);
  const [originMetaList, setOriginMetaList] = useState<DicomInfo[]>([]);
  const {
    makeMetaList,
    delMetaList,
    metaListStatus,
    metaListLocalValid,
    metaListChangeData,
    getDicomTagAll,
    errorHandler,
    checkValidAllClear,
  } = SettingCommonMethods();

  const { submitConfirmDialog } = useStaticDialog();
  const setDialogContents = useSetRecoilState(dialogContentsState);

  /**
   * 제품별 각각 다 들어갈 수 있는 모듈로 제품에 따라
   * api 경로 다르게 반환 하기 위한 함수
   */
  const getAPIUrl = () => {
    let productType = '';

    switch (PRODUCT_CODE) {
      case 'atrophy':
        productType = 'ad';
        break;
      case 'mn1q':
        productType = 'ni';
        break;
      case 'mpdia':
        productType = 'ipd';
        break;
      case 'stroke':
        productType = 'scs';
        break;
      case 'nm':
        productType = 'nm';
        break;
      case 'ctp':
        productType = 'ctp';
        break;
      case 'cta':
        productType = 'cta';
        break;
    }
    return `api/setting/${productType}/meta-tag`;
  };

  /**
   * dicom tag 가져오기 전체다이콤태그
   */
  const getDicomTag = async () => {
    const _result = await getDicomTagAll();
    setDicomTagList(_result.result);
  };

  /**
   * metaList 유효성 체크
   */
  const getCheckMetaTagValid = async (idx: number) => {
    if (!metaList[idx].tag || !metaList[idx].definition) return false;

    // 로컬 유효성 체크
    const _localValid = metaListLocalValid(idx, metaList);

    // 통과시 서버 유효성 체크
    if (_localValid) {
      // valid 체크
      const url = `api/sw/custom/tag/validate`;
      const token = localStorage.getItem('PROD_ADMIN_ACCESS_TOKEN') || undefined;
      const _params = {
        value: metaList[idx].value,
        dicomTagId: metaList[idx].dicomTagId,
      };

      await getFetcher({
        method: 'POST',
        accessToken: token,
        body: _params,
      })(url)
        .then(async (r) => {
          await metaListChange(r.code, idx, 'VALID');
        })
        .catch((e) => {
          console.log(e, 'e');
        });
    }
  };

  /**
   * metaList 변경 또는 유효성 확인
   * @param e
   * @param index
   * @param type
   */
  const metaListChange = useCallback(
    (e: any, index: number, type: string) => {
      metaListChangeData(e, index, type, metaList, originMetaList, setMetaList);
    },
    [metaList, originMetaList],
  );

  /**
   * submit confirm cb
   * data 전송
   */
  const postDataCB = async () => {
    // valid 체크
    const url = getAPIUrl();
    const token = localStorage.getItem('PROD_ADMIN_ACCESS_TOKEN') || undefined;
    const _tagList: any = [];

    metaList.forEach((data) => {
      const _tmp = {
        dicomTagId: data.dicomTagId,
        isNumericVR: data.isNumericVR,
        isSequential: data.isSequential,
        tag: data.tag,
        value: data.value,
        vr: data.vr,
      };
      _tagList.push(_tmp);
    });

    await getFetcher({
      method: 'POST',
      accessToken: token,
      body: { tags: _tagList },
    })(url)
      .then(async (r) => {
        if (r.code === '200') {
          setDialogContents({
            type: 'plain',
            body: `It has been reflected.`,
            footer: (
              <PrimaryButton
                onClick={async () => {
                  setDialogContents({ type: 'hidden' });
                }}
              >
                OK
              </PrimaryButton>
            ),
          });
          setOriginMetaList(metaList);
        } else {
          errorHandler(r);
        }
      })
      .catch((e) => {
        console.log(e, 'e');
      });
  };

  /**
   * get send to pacs data
   */
  const getData = async () => {
    const url = getAPIUrl(); // ok
    const token = localStorage.getItem('PROD_ADMIN_ACCESS_TOKEN') || undefined;

    await getFetcher({
      method: 'GET',
      accessToken: token,
    })(url)
      .then((r) => {
        if (r.code === '200') {
          r.result.tags.forEach((tag: { valid: boolean }) => {
            tag.valid = true;
          });
          setMetaList(r.result.tags);
          setOriginMetaList(r.result.tags);
        } else {
          errorHandler(r);
        }
      })
      .catch((e) => {
        console.log(e, 'e');
      });
  };

  /**
   * 최초 데이터 설정
   */
  useEffect(() => {
    (async () => {
      await getDicomTag();
      await getData();
    })();
  }, []);

  useEffect(() => {
    if (!metaList) {
      // send가 false 이면 기본 데이터 비교 해서 활성화 여부
      setIsChanged(metaList !== originMetaList);
    } else {
      // send가 true 이면 현재 변경 데이터 확인
      const _changeMetaList = checkValidAllClear(metaList, originMetaList);
      setIsChanged(_changeMetaList);
    }
  }, [metaList, originMetaList]);

  return (
    <>
      <label>Custom meta tag</label>

      <StyledMetatagItemWrap>
        <StyledMetatagBox className={metaList.length > 0 ? 'add-tag' : ''}>
          {/*
            StyledMetatagItem상태에 따라 클래스 추가/제거
            disable, active, error, success
          */}
          {metaList &&
            metaList.map((item, index) => {
              return (
                <StyledMetatagItem key={index} className={`report ${metaListStatus(item)}`}>
                  <StyledMetatag>
                    <div className="title">Metatag</div>
                    {/*검색*/}
                    <SelectSearchDicomTag
                      dicomTagList={dicomTagList}
                      value={item}
                      dataIdx={index}
                      onChangeFunc={metaListChange}
                    />
                  </StyledMetatag>
                  <StyledValue className="value">
                    <div className="title">Value</div>
                    <Input
                      type={`text`}
                      name={`tel5`}
                      placeholder={`Value`}
                      // className={item.isNumericVR ? 'sequence-type' : ''}
                      className={'sequence-type'}
                      value={he.decode(item.value || '')}
                      onChange={(e) => {
                        metaListChange(e, index, 'VALUE');
                      }}
                    />
                    {item.value && !item.valid && <p className="error-msg">Invalid value</p>}
                    {(PRODUCT_CODE === 'stroke' || PRODUCT_CODE === 'cta') && (
                      <>
                        <Input
                          className={'check-input'}
                          type={'checkbox'}
                          name={'Sequence'}
                          checked={item.isSequential}
                          onChange={(e) => {
                            metaListChange(e, index, 'SEQUENCE');
                          }}
                        >
                          {'Sequence'}
                        </Input>
                      </>
                    )}
                  </StyledValue>

                  {item.tag && item.value && !item.isChanged && item.valid ? (
                    <StyledTxtSuccess>
                      <span>Success!</span>
                    </StyledTxtSuccess>
                  ) : (
                    <StyledConfirmButton
                      className="bnt-confirm"
                      onClick={submitDebounce(async () => {
                        await getCheckMetaTagValid(index);
                      })}
                    >
                      Confirm
                    </StyledConfirmButton>
                  )}
                  <StyledcloseButton onClick={() => delMetaList(index, metaList, setMetaList)} />
                </StyledMetatagItem>
              );
            })}
        </StyledMetatagBox>

        <StyledMetatagAddButton onClick={() => makeMetaList(metaList, setMetaList)}>
          <span>Add metatag</span>
        </StyledMetatagAddButton>
      </StyledMetatagItemWrap>
      <StyledSwButtonSection>
        <PrimaryButton
          styles={css`
            width: 145px;
            height: 48px;
            font-size: 18px;
          `}
          onClick={submitDebounce(() => submitConfirmDialog(postDataCB))}
          disabled={!isChanged}
        >
          Submit
        </PrimaryButton>
      </StyledSwButtonSection>
    </>
  );
};

export default CustomMetaTag;
